<template>
    <div class="tw:hidden tw:xl:flex tw:items-center tw:gap-x-4">
        <router-link
            v-if="user"
            id="application_navigation_home"
            class="tw:rounded-sm tw:hover:bg-[var(--surface-hover)]"
            to="/"
        >
            <e-button
                type="secondary"
                class="tw:font-semibold tw:bg-transparent tw:border-none"
            >
                Dashboard
            </e-button>
        </router-link>

        <div
            v-if="canSeeCourseDropdown"
            class="dropdown-wrapper tw:rounded-sm tw:hover:bg-[var(--surface-hover)]"
            @mouseenter="showCoursesDropdown"
            @mouseleave="hideCoursesDropdown"
            @focusin="showCoursesDropdown"
            @focusout="hideCoursesDropdown"
        >
            <e-dropdown ref="coursesDropdown" scrollable max-height="max(0px, calc(100vh - 6rem))">
                <template #trigger>
                    <e-button
                        icon-right="angle-down"
                        type="secondary"
                        class="tw:font-semibold tw:bg-transparent tw:border-none"
                        :style="isCoursesActive ? { color: 'var(--p-primary-color)' } : {}"
                    >
                        Courses
                    </e-button>
                </template>
                <Menu
                    :model="coursesMenuItems"
                    @mouseenter="showCoursesDropdown"
                    @mouseleave="hideCoursesDropdown"
                    @focusin="showCoursesDropdown"
                    @focusout="hideCoursesDropdown"
                >
                    <template #item="{ item, props: itemProps }">
                        <router-link
                            v-if="item.route && item.allowed && !item.header"
                            v-slot="{ href, navigate }"
                            :to="item.route"
                            custom
                        >
                            <a
                                v-bind="itemProps.action"
                                class="tw:text-[var(--text-color)]! tw:font-medium!"
                                :class="{ 'tw:text-sm!': item.small }"
                                :href="href"
                                @click="event => navigateAndCloseSidebar(event, navigate, hideCoursesDropdown)"
                            >
                                <span class="ml-2 tw:text-base!">{{ item.label }}</span>
                            </a>
                        </router-link>
                        <div v-else-if="item.header" class="p-menuitem-header tw:text-base!">
                            {{ item.label }}
                        </div>
                    </template>
                </Menu>
            </e-dropdown>
        </div>

        <div
            v-if="canSeeAdministrationDropdown"
            class="dropdown-wrapper tw:rounded-sm tw:hover:bg-[var(--surface-hover)]"
            @mouseenter="showAdministrationDropdown"
            @mouseleave="hideAdministrationDropdown"
            @focusin="showAdministrationDropdown"
            @focusout="hideAdministrationDropdown"
        >
            <e-dropdown ref="administrationDropdown" scrollable max-height="max(0px, calc(100vh - 6rem))">
                <template #trigger>
                    <e-button
                        icon-right="angle-down"
                        type="secondary"
                        class="tw:font-semibold tw:bg-transparent tw:border-none"
                        :style="isAdministrationActive ? { color: 'var(--p-primary-color)' } : {}"
                    >
                        Administration
                    </e-button>
                </template>
                <Menu
                    :model="administrationMenuItems"
                    @mouseenter="showAdministrationDropdown"
                    @mouseleave="hideAdministrationDropdown"
                    @focusin="showAdministrationDropdown"
                    @focusout="hideAdministrationDropdown"
                >
                    <template #submenulabel="{ item }">
                        <span class="tw:text-[var(--text-color)] tw:font-semibold">{{ item.label }}</span>
                    </template>
                    <template #item="{ item, props: itemProps }">
                        <router-link
                            v-if="item.route && item.allowed"
                            v-slot="{ href, navigate }"
                            :to="item.route"
                            custom
                        >
                            <a
                                v-bind="itemProps.action"
                                :href="href"
                                class="tw:text-[var(--text-color)]! tw:font-medium!"
                                @click="
                                    event =>
                                        navigateAndCloseSidebar(event, navigate, hideAdministrationDropdown)
                                "
                            >
                                <span class="ml-2 tw:text-base!">{{ item.label }}</span>
                            </a>
                        </router-link>
                        <a
                            v-else-if="item.url && item.allowed"
                            v-bind="itemProps.action"
                            :href="item.url"
                            :target="item.target"
                        >
                            <span class="ml-2 tw:text-[var(--text-color)] tw:text-base!">{{ item.label }}</span>
                        </a>
                    </template>
                </Menu>
            </e-dropdown>
        </div>

        <div
            v-if="user"
            class="dropdown-wrapper tw:rounded-sm tw:hover:bg-[var(--surface-hover)]"
            @mouseenter="showResourcesDropdown"
            @mouseleave="hideResourcesDropdown"
            @focusin="showResourcesDropdown"
            @focusout="hideResourcesDropdown"
        >
            <e-dropdown id="application_navigation_resources" ref="resourcesDropdown" scrollable max-height="max(0px, calc(100vh - 6rem))">
                <template #trigger>
                    <e-button
                        icon-right="angle-down"
                        type="secondary"
                        class="tw:font-semibold tw:bg-transparent tw:border-none"
                        :style="isResourcesActive ? { color: 'var(--p-primary-color)' } : {}"
                    >
                        Resources
                    </e-button>
                </template>
                <Menu
                    :model="resourcesMenuItems"
                    @mouseenter="showResourcesDropdown"
                    @mouseleave="hideResourcesDropdown"
                    @focusin="showResourcesDropdown"
                    @focusout="hideResourcesDropdown"
                >
                    <template #item="{ item, props: itemProps }">
                        <router-link
                            v-if="item.route && item.allowed"
                            v-slot="{ href, navigate }"
                            :to="item.route"
                            custom
                        >
                            <a
                                v-bind="itemProps.action"
                                :href="href"
                                class="tw:text-[var(--text-color)]! tw:font-medium! tw:text-base!"
                                @click="
                                    event => navigateAndCloseSidebar(event, navigate, hideResourcesDropdown)
                                "
                            >
                                <span class="ml-2 tw:text-base!">{{ item.label }}</span>
                            </a>
                        </router-link>
                    </template>
                </Menu>
            </e-dropdown>
        </div>
    </div>
</template>

<script setup>
import intersection from 'lodash/intersection.js';
import some from 'lodash/some.js';
import { computed, getCurrentInstance, onMounted, onUnmounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useLayout } from '@/layouts/composables/layout.js';
import Page from '@/models/pages/Page.js';
import User from '@/models/User.js';
import Gate from '@/plugins/vue-gate/Gate.js';
import { gate } from '@/plugins/vue-gate/Index.js';
import { canManageLateSubmissionPolicies } from '@/policies/LateSubmissionPolicyPolicy.js';
import { canViewStudentSupportDashboard } from '@/policies/StudentSupportDashboardPolicy.js';
import { fetchUserEnrollmentsSingleFlight } from '@/utilities/fetchUserEnrollmentsSingleFlight.js';
import { isCurrentCourseEnrollment } from '@/utilities/isCurrentCourseEnrollment.js';
import Utilities from '@/utilities/Utilities.js';

defineOptions({
    name: 'StanbridgeTopbarLinks',
});

const { closeMenu } = useLayout();
const router = useRouter();
const store = useStore();
const instance = getCurrentInstance();
const { $hasFeature } = instance.appContext.config.globalProperties;

const user = computed(() => {
    return User.query().with(Gate.userRelationships()).find(store.state.Auth.user?.id);
});

const enrolledCourses = ref([]);
const currentEnrollments = computed(() => enrolledCourses.value.filter(isCurrentCourseEnrollment));

const coursesDropdown = ref(null);
const coursesHideTimeout = ref(null);
const administrationDropdown = ref(null);
const administrationHideTimeout = ref(null);
const resourcesDropdown = ref(null);
const resourcesHideTimeout = ref(null);

const canSeeCourseDropdown = computed(() => {
    if (!user.value) return false;

    return (
        gate.allows('viewAny', 'Course') ||
        gate.allows('viewAny', 'CourseTemplate') ||
        user.value.isStudentSupport() ||
        gate.allows('viewAny', 'Meeting') ||
        currentEnrollments.value.length > 0
    );
});

const canViewElearningPackages = computed(() => {
    return gate.allows('viewAny', 'ElearningPackage') && $hasFeature('ELEARNING_PACKAGES');
});

const canSeeAdministrationDropdown = computed(() => {
    if (!user.value) return false;

    return some([
        canManageLateSubmissionPolicies(user.value),
        gate.allows('viewAny', 'Ebook'),
        gate.allows('viewAny', 'Video'),
        canViewElearningPackages.value,
        gate.allows('viewAny', 'AttemptAuthorization'),
        gate.allows('viewAny', 'RubricBank'),
        gate.allows('viewAny', 'Remediation'),
        gate.allows('viewAny', 'SlideDeck'),
        gate.allows('viewAny', 'User'),
        gate.allows('viewAny', 'Integrations'),
        gate.allows('viewAny', 'Quiz'),
        gate.allows('viewAny', 'Page'),
        gate.allows('viewAny', 'SlideDeck'),
        gate.allows('viewAny', 'RubricBank'),
        gate.allows('viewAny', 'Group'),
        gate.allows('viewAny', 'Location'),
        gate.allows('_viewRiskAssessment', 'Report'),
        gate.allows('viewAny', 'Report'),
        gate.userCan('view the activity log'),
        gate.userCan('view pulse'),
        gate.userCan('view websockets'),
    ]);
});

const showHelpNavLink = computed(() => {
    if (store.getters['InstitutionSettings/get']('support_documentation_enabled')) {
        return true;
    }

    return gate.userCan('view all help articles');
});

const pageAuthorizedRoleIds = computed(() => {
    return Page.getPageAuthorizedRoleIds();
});

const showEditPageNav = computed(() => {
    if (!user.value) return false;

    if (store.getters['User/hasRole']('Admin') || store.getters['User/hasRole']('Owner')) return true;

    if (!store.getters['InstitutionSettings/get']('page_enabled')) return false;

    const userGroupRoleIds = user.value.user_groups.map(group => group.role_id);

    return intersection(userGroupRoleIds, pageAuthorizedRoleIds.value).length > 0 || gate.allows('viewAny', 'Page');
});

const canSeeDashboardsNavLink = computed(() => {
    if (!user.value) return false;

    return (
        canViewStudentSupportDashboard(user.value) ||
        gate.userCan('view a dashboard') ||
        gate.userCan('view insight dashboard') ||
        gate.userCanViaGroup('view insight dashboard') ||
        user.value.isGroupInstructor()
    );
});

const canSeeProctorDashboardNavLink = computed(() => {
    return (
        gate.userCan("create another user's quiz attempt") || gate.userCanViaGroup("create another user's quiz attempt")
    );
});

const coursesMenuItems = computed(() => {
    const topLevelItems = [
        {
            label: 'Course Templates',
            route: { name: 'course-templates' },
            allowed: gate.allows('viewAny', 'CourseTemplate'),
        },
        {
            label: 'All Courses',
            route: { name: 'courses' },
            allowed: gate.allows('viewAny', 'Course'),
        },
        {
            label: 'Course Codes',
            route: { name: 'course-codes' },
            allowed: gate.allows('viewAny', 'CourseCode'),
        },
        {
            label: 'Meetings',
            route: { name: 'meetings-index' },
            allowed: gate.allows('viewAny', 'Meeting'),
        },
        {
            label: 'Due Dates and Schedules',
            route: { name: 'activities-view-upcoming' },
            allowed: gate.allows('view', 'Activity') && gate.allows('viewAny', 'TimePeriod'),
        },
    ].sort((a, b) => a.label.localeCompare(b.label));

    const items = [...topLevelItems];

    const sortedCurrentEnrollments = [...currentEnrollments.value].sort((a, b) =>
        (a.course?.title || '').localeCompare(b.course?.title || '')
    );

    if (sortedCurrentEnrollments.length > 0) {
        if (items.some(item => item.allowed)) {
            items.push({ separator: true });
        }

        items.push({
            label: 'Current Courses',
            route: { name: 'courses' },
            allowed: gate.allows('viewAny', 'Course'),
            small: true,
        });

        sortedCurrentEnrollments.forEach(({ course }) => {
            items.push({
                label: course.title,
                route: { name: 'course-dashboard', params: { courseId: course.id } },
                allowed: true,
                small: true,
            });
        });
    }

    return items.filter(item => item.separator || item.header || item.allowed);
});

const inboxEnabled = computed(() => {
    return $hasFeature('INBOX');
});

const resourcesMenuItems = computed(() => {
    return [
        {
            label: 'Help & Support',
            route: { name: 'help-home' },
            allowed: showHelpNavLink.value,
        },
        {
            label: 'Inbox',
            route: { name: 'inbox' },
            allowed: inboxEnabled.value,
        },
        {
            label: 'My Notes',
            route: { name: 'notes-home' },
            allowed: true,
        },
    ].sort((a, b) => a.label.localeCompare(b.label));
});

const administrationMenuItems = computed(() => {
    const mainSectionItems = [
        {
            label: 'Activity Log',
            route: { name: 'activity-log-index' },
            allowed: gate.userCan('view the activity log'),
        },
        {
            label: 'Impersonation Log',
            route: { name: 'impersonation-log-index' },
            allowed: $hasFeature('USER_IMPERSONATION') && gate.userCan('view the impersonation log'),
        },
        {
            label: 'Attempt Authorizations',
            route: { name: 'attempt-authorizations-index' },
            allowed: gate.allows('viewAny', 'AttemptAuthorization'),
        },
        {
            label: 'Dashboards',
            route: { name: 'dashboards' },
            allowed: canSeeDashboardsNavLink.value,
        },
        // Only include Content Library when eLearning Packages is accessible — PrimeVue Menu
        // renders nested parents via `visible`/`items`, not our custom `allowed` flag, so an
        // always-present parent would leave an orphaned "Content Library" submenu label.
        ...(canViewElearningPackages.value
            ? [
                  {
                      label: 'Content Library',
                      items: [
                          {
                              label: 'eLearning Packages',
                              route: { name: 'elearning-packages-index' },
                              activeRouteNames: ['elearning-packages-show'],
                              allowed: true,
                          },
                      ],
                  },
              ]
            : []),
        {
            label: 'Evaluation Templates',
            route: { name: 'evaluation-templates-index' },
            allowed: gate.allows('viewAny', 'EvaluationTemplate'),
        },
        {
            label: 'Groups',
            route: { name: 'groups-index' },
            allowed: gate.allows('viewAny', 'Group'),
        },
        {
            label: 'Ebooks',
            route: { name: 'ebooks-index' },
            allowed: gate.allows('viewAny', 'Ebook'),
        },
        {
            label: 'Integrations',
            route: { name: 'integrations-index' },
            allowed: gate.allows('viewAny', 'Integrations'),
        },
        ...(canManageLateSubmissionPolicies(user.value)
            ? [
                  {
                      label: 'Late Submission Policies',
                      route: { name: 'late-submission-policies-index' },
                      activeRouteNames: ['late-submission-policies-show'],
                      allowed: true,
                  },
              ]
            : []),
        {
            label: 'Locations',
            route: { name: 'locations-index' },
            allowed: gate.allows('viewAny', 'Location'),
        },
        {
            label: 'Pages',
            route: { name: 'edit-pages-index' },
            allowed: showEditPageNav.value,
        },
        {
            label: 'People',
            route: { name: 'people-index' },
            allowed: gate.allows('viewAny', 'User'),
        },
        {
            label: 'Institution Settings',
            route: { name: 'institution-settings-profile' },
            allowed: gate.userCan('view an institution setting'),
        },
        {
            label: 'Proctor Dashboard',
            route: { name: 'proctor-dashboard-quiz-lobby' },
            allowed: canSeeProctorDashboardNavLink.value,
        },
        {
            label: 'Remediations',
            route: { name: 'remediations-index' },
            allowed: gate.allows('viewAny', 'Remediation'),
        },
        {
            label: 'Reports',
            route: { name: 'reports-index' },
            allowed: gate.allows('viewAny', 'Report'),
        },
        {
            label: 'Risk Assessment Report',
            route: { name: 'risk-assessment-report' },
            allowed: gate.userCan('view risk assessment report') && !gate.allows('viewAny', 'Report'),
        },
        {
            label: 'Rubric Templates',
            route: { name: 'rubric-bank-index' },
            allowed: gate.allows('viewAny', 'RubricBank'),
        },
        {
            label: 'Slide Decks',
            route: { name: 'slide-decks-index' },
            allowed: gate.allows('viewAny', 'SlideDeck'),
        },
        {
            label: 'Surveys',
            route: { name: 'surveys-index' },
            allowed: gate.allows('viewAny', 'Quiz'),
        },
        {
            label: 'Videos',
            route: { name: 'videos-index' },
            allowed: gate.allows('viewAny', 'Video'),
        },
        {
            label: 'Quizzes',
            route: { name: 'quizzes-index' },
            allowed: gate.allows('viewAny', 'Quiz'),
        },
    ].sort((a, b) => a.label.localeCompare(b.label));

    const superAdminSectionItems = [
        {
            label: 'Horizon',
            url: '/horizon',
            target: '_blank',
            allowed: gate.userCan('view horizon'),
        },
        {
            label: 'Pulse',
            url: '/pulse',
            target: '_blank',
            allowed: gate.userCan('view pulse'),
        },
    ].sort((a, b) => a.label.localeCompare(b.label));

    return [...mainSectionItems, { separator: true }, ...superAdminSectionItems];
});

const isCoursesActive = computed(() => {
    const currentRouteName = router.currentRoute.value.name;
    return coursesMenuItems.value.some(item => item.route && item.route.name === currentRouteName);
});

function menuContainsRoute(items, routeName) {
    return items.some(
        item =>
            item.route?.name === routeName ||
            item.activeRouteNames?.includes(routeName) ||
            (item.items && menuContainsRoute(item.items, routeName))
    );
}

const isAdministrationActive = computed(() => {
    const currentRouteName = router.currentRoute.value.name;
    return menuContainsRoute(administrationMenuItems.value, currentRouteName);
});

const isResourcesActive = computed(() => {
    const currentRouteName = router.currentRoute.value.name;
    return resourcesMenuItems.value.some(item => item.route && item.route.name === currentRouteName);
});

async function loadEnrollments(forceRefresh = false) {
    const preloaded = Utilities.initialState('UserEnrollments');
    const isDebug = import.meta.env.VITE_APP_DEBUG === 'true';

    if (preloaded?.data?.length && !forceRefresh) {
        enrolledCourses.value = preloaded.data;
    }

    if (!enrolledCourses.value.length || isDebug || forceRefresh) {
        try {
            const { data } = await fetchUserEnrollmentsSingleFlight({ force: forceRefresh });

            enrolledCourses.value = data.data;
        } catch (error) {
            console.error('Failed to load enrollments:', error);
        }
    }
}

function navigateAndCloseSidebar($event, navigate, hideDropdown) {
    hideDropdown();
    if (window.innerWidth < 1200) {
        closeMenu();
    }
    navigate($event);
}

function showCoursesDropdown() {
    if (coursesHideTimeout.value) {
        clearTimeout(coursesHideTimeout.value);
        coursesHideTimeout.value = null;
    }
    coursesDropdown.value?.show();
}

function hideCoursesDropdown() {
    coursesHideTimeout.value = setTimeout(() => {
        coursesDropdown.value?.hide();
        coursesHideTimeout.value = null;
    }, 100);
}

function showAdministrationDropdown() {
    if (administrationHideTimeout.value) {
        clearTimeout(administrationHideTimeout.value);
        administrationHideTimeout.value = null;
    }
    administrationDropdown.value?.show();
}

function hideAdministrationDropdown() {
    administrationHideTimeout.value = setTimeout(() => {
        administrationDropdown.value?.hide();
        administrationHideTimeout.value = null;
    }, 100);
}

function showResourcesDropdown() {
    if (resourcesHideTimeout.value) {
        clearTimeout(resourcesHideTimeout.value);
        resourcesHideTimeout.value = null;
    }
    resourcesDropdown.value?.show();
}

function hideResourcesDropdown() {
    resourcesHideTimeout.value = setTimeout(() => {
        resourcesDropdown.value?.hide();
        resourcesHideTimeout.value = null;
    }, 100);
}

watch(
    user,
    (newUser, oldUser) => {
        if (newUser && (!oldUser || oldUser.id !== newUser.id)) {
            loadEnrollments();
        }
    },
    { immediate: true }
);

function forceReloadEnrollments() {
    loadEnrollments(true);
}

onMounted(() => {
    window.addEventListener('enrollments-refresh', forceReloadEnrollments);
});

onUnmounted(() => {
    window.removeEventListener('enrollments-refresh', forceReloadEnrollments);

    if (coursesHideTimeout.value) {
        clearTimeout(coursesHideTimeout.value);
    }
    if (administrationHideTimeout.value) {
        clearTimeout(administrationHideTimeout.value);
    }
    if (resourcesHideTimeout.value) {
        clearTimeout(resourcesHideTimeout.value);
    }
});
</script>